import { cn } from '../ui/utils';
import { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  /**
   * Test ID for testing purposes
   * @default 'empty-state'
   */
  testId?: string;
}

/**
 * EmptyState - Displays empty state with icon, title, description and optional action
 * 
 * Used when lists/tables have no data to show. Follows WCAG guidelines for status messages.
 * 
 * @example
 * ```tsx
 * <EmptyState
 *   icon={Package}
 *   title="Nenhum item encontrado"
 *   description="Adicione seu primeiro item para começar"
 *   action={<Button>Adicionar Item</Button>}
 * />
 * ```
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  testId = 'empty-state',
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
      data-testid={testId}
      role="status"
      aria-label={title}
    >
      {Icon && (
        <div className="mb-4 rounded-full bg-muted p-4" aria-hidden="true">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      <h3 className="mb-2" data-testid={`${testId}-title`}>{title}</h3>
      {description && (
        <p 
          className="mb-6 max-w-md text-muted-foreground"
          data-testid={`${testId}-description`}
        >
          {description}
        </p>
      )}
      {action && (
        <div data-testid={`${testId}-action`}>
          {action}
        </div>
      )}
    </div>
  );
}
